import { msg } from "@lingui/core/macro";
import {
  buildSalesReportDataset,
  type CategorySales,
  exportDataset,
  type ExportFormat,
  formatMoney,
  getCategoriesQuery,
  getEmployeesQuery,
  getProductsQuery,
  getReportLinesQuery,
  getReportOrdersQuery,
  getReportPaymentsQuery,
  type PaymentMethodSales,
  queryKeys,
  type ReportLine,
  type ReportOrder,
  type ReportPayment,
  salesByCategory,
  salesByDay,
  salesByEmployee,
  salesByHour,
  salesByPaymentMethod,
  salesByProduct,
  summarize,
} from "@comandero/shared";
import type {
  Branch,
  Employee,
  MenuCategory,
  MenuProduct,
  Order,
  OrderLine,
  Organization,
  Payment,
} from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createMemo, For, type JSX, Show } from "solid-js";
import { Link, useNavigate } from "@tanstack/solid-router";
import { ChevronRight, Download } from "@fleet/icons";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import { BarChart, ColumnChart, DonutChart, LineChart } from "@/components/ui/charts";
import { EmptyState } from "@/components/ui/empty-state";
import { buttonVariants } from "@/components/ui/button";

/** Date ranges addressable by URL (`?range=`, campaign #15b). */
export const REPORT_RANGES = ["all", "today", "7d", "30d"] as const;
export type ReportRange = (typeof REPORT_RANGES)[number];

interface ReportsDashboardProps {
  org: () => Organization | null;
  branch: () => Branch | null;
  /** Date range from URL (`?range=`). Born addressable.*/
  rangeParam?: () => ReportRange | undefined;
  /** Writes range to URL (`replace`).*/
  onRangeChange?: (range: ReportRange) => void;
}

/** Lower bound (ms epoch) of the range, or null for "all". */
function rangeStart(range: ReportRange, now: number): number | null {
  const DAY_MS = 86_400_000;
  switch (range) {
    case "today": {
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }
    case "7d": {
      return now - 7 * DAY_MS;
    }
    case "30d": {
      return now - 30 * DAY_MS;
    }
    default: {
      return null;
    }
  }
}

const PAYMENT_METHOD_LABELS: Record<string, ReturnType<typeof msg>> = {
  cash: msg`Cash`,
  card: msg`Card`,
  transfer: msg`Transfer`,
  other: msg`Other`,
  uncategorized: msg`Unspecified`,
};

export function ReportsDashboard(props: ReportsDashboardProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const orgId = (): string | null => props.org()?.id ?? null;
  const branchId = (): string | null => props.branch()?.id ?? null;
  // Active range: URL (`?range=`) governs; defaults to "all".
  const range = (): ReportRange => props.rangeParam?.() ?? "all";

  // "All" range (no date filter): report is deterministic from
  // rows. A range selector is mounted above filtering these same rows.
  const orders = createPowerSyncQuery<Order>({
    queryKey: () => queryKeys.comandero.reportOrders(branchId() ?? "none", "all"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      const b = branchId();
      return d && id && b ? getReportOrdersQuery(d, id, b) : null;
    },
    db,
    enabled: () => Boolean(orgId()) && Boolean(branchId()),
  });

  const lines = createPowerSyncQuery<OrderLine>({
    queryKey: () => queryKeys.comandero.reportLines(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getReportLinesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const payments = createPowerSyncQuery<Payment>({
    queryKey: () => queryKeys.comandero.reportPayments(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getReportPaymentsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const employees = createPowerSyncQuery<Employee>({
    queryKey: () => queryKeys.comandero.employees(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getEmployeesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const products = createPowerSyncQuery<MenuProduct>({
    queryKey: () => queryKeys.comandero.products(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getProductsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const categories = createPowerSyncQuery<MenuCategory>({
    queryKey: () => queryKeys.comandero.categories(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCategoriesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  // product_id → { categoryId, categoryName } to enrich lines.
  const productCategory = createMemo(() => {
    const catName = new Map<string, string>(
      (categories.data ?? []).map((c) => [c.id, c.name ?? ""]),
    );
    const map = new Map<string, { categoryId: string | null; categoryName: string | null }>();
    for (const p of products.data ?? []) {
      map.set(p.id, {
        categoryId: p.category_id ?? null,
        categoryName: p.category_id ? (catName.get(p.category_id) ?? null) : null,
      });
    }
    return map;
  });

  // Adapt DB rows to PURE report shape (deterministic calc).
  const reportOrders = createMemo<ReportOrder[]>(() =>
    (orders.data ?? []).map((o) => ({
      id: o.id,
      status: o.status,
      total: o.total,
      employeeId: o.employee_id,
      closedAt: o.closed_at ?? o.opened_at,
    })),
  );
  const reportLines = createMemo<ReportLine[]>(() => {
    const pc = productCategory();
    return (lines.data ?? []).map((l) => {
      const cat = l.product_id ? pc.get(l.product_id) : undefined;
      return {
        orderId: l.order_id ?? "",
        productId: l.product_id,
        name: l.name,
        qty: l.qty,
        lineTotal: l.line_total,
        categoryId: cat?.categoryId ?? null,
        categoryName: cat?.categoryName ?? null,
      };
    });
  });
  const reportPayments = createMemo<ReportPayment[]>(() =>
    (payments.data ?? []).map((p) => ({
      orderId: p.order_id ?? "",
      amount: p.amount,
      tip: p.tip,
      employeeId: p.employee_id,
      method: p.method,
    })),
  );

  // Range filter: trims orders by `closedAt` and propagates trim to
  // lines/payments by membership. Deterministic calc, addressable via `?range=`.
  const filteredOrders = createMemo<ReportOrder[]>(() => {
    const start = rangeStart(range(), Date.now());
    const all = reportOrders();
    if (start == null) {
      return all;
    }
    return all.filter((o) => {
      const ts = o.closedAt ? Date.parse(o.closedAt) : Number.NaN;
      return Number.isFinite(ts) && ts >= start;
    });
  });
  const filteredOrderIds = createMemo(() => new Set(filteredOrders().map((o) => o.id)));
  const filteredLines = createMemo<ReportLine[]>(() => {
    if (range() === "all") {
      return reportLines();
    }
    const ids = filteredOrderIds();
    return reportLines().filter((l) => ids.has(l.orderId));
  });
  const filteredPayments = createMemo<ReportPayment[]>(() => {
    if (range() === "all") {
      return reportPayments();
    }
    const ids = filteredOrderIds();
    return reportPayments().filter((p) => ids.has(p.orderId));
  });

  const summary = createMemo(() => summarize(filteredOrders(), filteredPayments()));
  const byProduct = createMemo(() => salesByProduct(filteredOrders(), filteredLines()));
  const byEmployee = createMemo(() => salesByEmployee(filteredOrders(), filteredPayments()));
  const byHour = createMemo(() => salesByHour(filteredOrders()));
  const byCategory = createMemo(() => salesByCategory(filteredOrders(), filteredLines()));
  const byMethod = createMemo(() => salesByPaymentMethod(filteredOrders(), filteredPayments()));
  const byDay = createMemo(() => salesByDay(filteredOrders()));

  const hasSales = createMemo(() => summary().ticketCount > 0);
  // Are there sales in ANY range? Decides whether to show range selector
  // (with data to filter) or global empty-state (new account without sales).
  const hasAnyData = createMemo(() => reportOrders().some((o) => o.status !== "void"));

  const rangeLabels = (): Record<ReportRange, string> => ({
    all: t(msg`All time`),
    today: t(msg`Today`),
    "7d": t(msg`7 days`),
    "30d": t(msg`30 days`),
  });

  const employeeName = (id: string): string => {
    if (id === "unassigned") {
      return t(msg`Unassigned`);
    }
    return employees.data?.find((e) => e.id === id)?.name ?? id;
  };

  const categoryLabel = (c: CategorySales): string =>
    c.categoryId === "uncategorized" || !c.name ? t(msg`Uncategorized`) : c.name;

  const methodLabel = (m: PaymentMethodSales): string => {
    const key = PAYMENT_METHOD_LABELS[m.method];
    return key ? t(key) : m.method;
  };

  // K1 — Export of VISIBLE report (same filtered range) to CSV / PDF. The
  // adapter lives in @comandero/shared: rows = product breakdown, and the
  // summary (tickets/sales/tips/voids) travels as document metadata.
  // PLUGIN-KERNEL: HOW the file reaches the user (blob download on the web,
  // native save dialog on the desktop) is decided once by the export plugin.
  // `undefined` means no host can deliver a file here — an SSR render — and
  // the button simply does nothing rather than throwing.
  const delivery = useKernelService(EXPORT_DELIVERY);

  const handleExport = (format: ExportFormat) => {
    const target = delivery();
    if (!target) {
      return;
    }
    const rangeLabel = rangeLabels()[range()];
    const branchName = props.branch()?.name ?? "";
    const dataset = buildSalesReportDataset(summary(), byProduct(), {
      title: t(msg`Sales report`),
      subtitle: `${rangeLabel}${branchName ? ` · ${branchName}` : ""}`,
    });
    void exportDataset(dataset, format, target);
  };

  return (
    <Show
      when={hasAnyData()}
      fallback={
        <EmptyState
          illustration="orders"
          data-testid="reports-empty"
          title={t(msg`No sales to report yet`)}
          description={t(
            msg`Once you charge your first orders, sales charts and breakdowns will appear here.`,
          )}
          onIconActivate={() => void navigate({ to: "/pos" })}
          iconActionLabel={t(msg`Open point of sale`)}
          action={
            <Link to="/pos" class={buttonVariants()}>
              {t(msg`Open point of sale`)}
            </Link>
          }
        />
      }
    >
      <div class="flex flex-col gap-6">
        {/* Selector de rango — addressable por URL (`?range=`, #15b) + export K1. */}
        <div class="flex flex-wrap items-center gap-1.5">
          <div
            class="flex flex-wrap gap-1.5"
            role="group"
            aria-label={t(msg`Date range`)}
            data-testid="reports-range-selector"
          >
            <For each={REPORT_RANGES}>
              {(r) => (
                <button
                  type="button"
                  data-testid={`reports-range-${r}`}
                  data-active={range() === r ? "true" : undefined}
                  aria-pressed={range() === r}
                  class="rounded-full border px-3 py-1 text-sm transition-colors"
                  classList={{
                    "border-primary bg-primary text-primary-foreground": range() === r,
                    "border-border/40 text-muted-foreground hover:bg-muted/40": range() !== r,
                  }}
                  onClick={() => props.onRangeChange?.(r)}
                >
                  {rangeLabels()[r]}
                </button>
              )}
            </For>
          </div>

          {/* K1: export del reporte visible (oculto sin ventas en el rango). */}
          <Show when={hasSales()}>
            <div class="ml-auto flex items-center gap-2">
              <button
                type="button"
                class={buttonVariants({ variant: "ghost", size: "sm" })}
                onClick={() => handleExport("csv")}
                data-testid="export-csv-button"
              >
                <Download class="h-4 w-4" />
                CSV
              </button>
              <button
                type="button"
                class={buttonVariants({ variant: "ghost", size: "sm" })}
                onClick={() => handleExport("pdf")}
                data-testid="export-pdf-button"
              >
                <Download class="h-4 w-4" />
                PDF
              </button>
            </div>
          </Show>
        </div>

        <Show
          when={hasSales()}
          fallback={
            <EmptyState
              illustration="orders"
              data-testid="reports-range-empty"
              title={t(msg`No sales in this range`)}
              description={t(msg`Try a wider date range to see sales for this period.`)}
              onIconActivate={() => props.onRangeChange?.("all")}
              iconActionLabel={t(msg`Show all time`)}
              action={
                <button
                  type="button"
                  class={buttonVariants()}
                  onClick={() => props.onRangeChange?.("all")}
                >
                  {t(msg`Show all time`)}
                </button>
              }
            />
          }
        >
          <div class="flex flex-col gap-8" data-testid="reports-dashboard">
            {/* KPI cards */}
            <section class="grid grid-cols-2 gap-3 md:grid-cols-5">
              <Kpi
                label={t(msg`Sales`)}
                value={formatMoney(summary().grossSales)}
                testid="kpi-sales"
              />
              <Kpi
                label={t(msg`Tickets`)}
                value={String(summary().ticketCount)}
                testid="kpi-tickets"
              />
              <Kpi
                label={t(msg`Average ticket`)}
                value={formatMoney(summary().averageTicket)}
                testid="kpi-average"
              />
              <Kpi label={t(msg`Tips`)} value={formatMoney(summary().tips)} testid="kpi-tips" />
              <Kpi label={t(msg`Voids`)} value={String(summary().voidCount)} testid="kpi-voids" />
            </section>

            {/* Sales over time */}
            <Show when={byDay().length > 1}>
              <ChartCard title={t(msg`Sales by day`)}>
                <LineChart
                  ariaLabel={t(msg`Sales by day`)}
                  data={byDay().map((d) => ({
                    label: d.day,
                    value: d.total,
                    display: formatMoney(d.total),
                  }))}
                />
              </ChartCard>
            </Show>

            {/* By hour — column chart */}
            <ChartCard title={t(msg`Sales by hour`)} testid="report-by-hour">
              <Show
                when={byHour().length > 0}
                fallback={<p class="text-sm text-muted-foreground">{t(msg`No sales yet`)}</p>}
              >
                <ColumnChart
                  ariaLabel={t(msg`Sales by hour`)}
                  data={byHour().map((h) => ({
                    label: `${String(h.hour).padStart(2, "0")}h`,
                    value: h.total,
                    display: formatMoney(h.total),
                  }))}
                />
              </Show>
            </ChartCard>

            <div class="grid gap-8 md:grid-cols-2">
              {/* Payment methods — donut */}
              <ChartCard title={t(msg`Payment methods`)} testid="report-by-method">
                <DonutChart
                  ariaLabel={t(msg`Sales by payment method`)}
                  centerLabel={
                    <>
                      <span class="text-xs text-muted-foreground">{t(msg`Total`)}</span>
                      <span class="text-sm font-semibold tabular-nums">
                        {formatMoney(byMethod().reduce((acc, m) => acc + m.total, 0))}
                      </span>
                    </>
                  }
                  data={byMethod().map((m) => ({
                    label: methodLabel(m),
                    value: m.total,
                    display: formatMoney(m.total),
                  }))}
                />
              </ChartCard>

              {/* By category — bar chart, deep-link a /menu */}
              <ChartCard
                title={t(msg`Sales by category`)}
                testid="report-by-category"
                action={
                  <Link
                    to="/menu"
                    class="flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                    data-testid="reports-link-menu"
                  >
                    {t(msg`Manage menu`)}
                    <ChevronRight class="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                }
              >
                <Show
                  when={byCategory().length > 0}
                  fallback={<p class="text-sm text-muted-foreground">{t(msg`No sales yet`)}</p>}
                >
                  <BarChart
                    ariaLabel={t(msg`Sales by category`)}
                    data={byCategory().map((c) => ({
                      label: categoryLabel(c),
                      value: c.total,
                      display: formatMoney(c.total),
                    }))}
                  />
                </Show>
              </ChartCard>
            </div>

            {/* Top products — bar chart, interconexión a /menu */}
            <ChartCard
              title={t(msg`Top products`)}
              action={
                <Link
                  to="/menu"
                  class="flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                >
                  {t(msg`View menu`)}
                  <ChevronRight class="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              }
            >
              <ul class="sr-only" data-testid="report-by-product">
                <For each={byProduct()}>
                  {(p) => (
                    <li data-testid="report-product-row" data-product-id={p.productId}>
                      <span>
                        {p.name} ×{p.qty}
                      </span>
                      <span data-testid="report-product-total">{formatMoney(p.total)}</span>
                    </li>
                  )}
                </For>
              </ul>
              <BarChart
                monochrome
                ariaLabel={t(msg`Top products by sales`)}
                data={byProduct()
                  .slice(0, 8)
                  .map((p) => ({
                    label: `${p.name} ·${p.qty}`,
                    value: p.total,
                    display: formatMoney(p.total),
                  }))}
              />
            </ChartCard>

            {/* By employee — interconexión a /staff */}
            <ChartCard
              title={t(msg`Sales by employee`)}
              action={
                <Link
                  to="/staff"
                  class="flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                >
                  {t(msg`View staff`)}
                  <ChevronRight class="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              }
            >
              <ul class="flex flex-col gap-1.5" data-testid="report-by-employee">
                <For each={byEmployee()}>
                  {(e) => (
                    <li
                      class="flex items-center justify-between gap-2 text-sm"
                      data-testid="report-employee-row"
                      data-employee-id={e.employeeId}
                    >
                      <span class="min-w-0 truncate">
                        {employeeName(e.employeeId)}{" "}
                        <span class="text-muted-foreground">({e.orders})</span>
                      </span>
                      <span class="shrink-0 tabular-nums" data-testid="report-employee-total">
                        {formatMoney(e.total)}
                      </span>
                    </li>
                  )}
                </For>
              </ul>
            </ChartCard>
          </div>
        </Show>
      </div>
    </Show>
  );
}

function ChartCard(props: {
  title: string;
  action?: JSX.Element;
  testid?: string;
  children: JSX.Element;
}) {
  return (
    <section class="rounded-2xl border border-border/40 bg-card/40 p-5" data-testid={props.testid}>
      <div class="mb-4 flex items-center justify-between gap-2">
        <h2 class="text-base font-semibold">{props.title}</h2>
        <Show when={props.action}>{props.action}</Show>
      </div>
      {props.children}
    </section>
  );
}

function Kpi(props: { label: string; value: string; testid: string }) {
  return (
    <div class="rounded-xl border border-border/40 bg-card/40 p-3" data-testid={props.testid}>
      <div class="text-xs text-muted-foreground">{props.label}</div>
      <div class="text-lg font-semibold tabular-nums" data-testid={`${props.testid}-value`}>
        {props.value}
      </div>
    </div>
  );
}
